/**
 * Actions for the selected own object: Breyta (text), Breyta áfangastað / Prófa (connector), A− / A+
 * (text, symbol and connector pin size), Litur, Afrita, Eyða. One row that scrolls sideways on a phone.
 */
import { Copy, Crosshair, Palette, Play, SquarePen, Trash2, X, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { HIGHLIGHT_COLORS, INK_COLORS, PEN_COLORS } from '../../shared/annotations';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { isSized, sizeOf } from './geometry';
import { ColorSwatches } from './ToolOptions';
import type { Annotate } from './useAnnotate';

function BarButton({ icon: Icon, label, onClick, danger }: { icon: LucideIcon; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'flex min-h-12 min-w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl px-2 text-[12px] font-semibold transition sm:flex-row sm:gap-1.5 sm:px-3 sm:text-[15px]',
        danger ? 'text-[var(--danger-text)] hover:bg-red-50 dark:hover:bg-red-950/40' : 'text-[var(--ink-2)] hover:bg-[var(--accent-soft)]',
      )}
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

/** "A−" / "A+": one size step; disabled at the model's limit. */
function SizeButton({ grow, label, disabled, onClick }: { grow: boolean; label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex size-12 shrink-0 items-center justify-center rounded-xl font-bold text-[var(--ink-2)] transition hover:bg-[var(--accent-soft)] disabled:opacity-35"
    >
      <span aria-hidden className={cx('leading-none whitespace-nowrap', grow ? 'text-[21px]' : 'text-[15px]')}>
        {grow ? 'A+' : 'A\u2212'}
      </span>
    </button>
  );
}

export function SelectionBar({ ann }: { ann: Annotate }) {
  const o = ann.selected;
  if (!o || ann.flow || ann.text) return null;
  return <SelectionBarFor key={o.id} ann={ann} />;
}

function SelectionBarFor({ ann }: { ann: Annotate }) {
  const t = useT();
  const [colors, setColors] = useState(false);
  const o = ann.selected!;
  return (
    <div className="pointer-events-none absolute z-30 flex justify-center px-2" style={{ left: 'var(--annot-left, 0px)', right: 'var(--annot-right, 0px)', top: 'calc(var(--annot-top, 0px) + 3.5rem)' }}>
      <div
        role="toolbar"
        aria-label={t('annotate.sel_actions')}
        className="pointer-events-auto flex max-w-full items-center gap-0.5 overflow-x-auto rounded-2xl bg-[var(--surface)] p-1 shadow-[0_6px_24px_rgb(0_0_0/0.22)] ring-1 ring-[var(--line)]"
      >
        {colors ? (
          <ColorSwatches
            colors={o.kind === 'highlight' ? HIGHLIGHT_COLORS : o.kind === 'ink' ? INK_COLORS : PEN_COLORS}
            value={o.data.color}
            onChange={(c) => {
              ann.recolorSelected(c);
              setColors(false);
            }}
          />
        ) : (
          <>
            {o.kind === 'connector' ? (
              <>
                <BarButton icon={Crosshair} label={t('annotate.conn_retarget')} onClick={ann.retargetConnector} />
                <BarButton icon={Play} label={t('annotate.conn_test')} onClick={ann.testConnector} />
              </>
            ) : null}
            {o.kind === 'text' ? <BarButton icon={SquarePen} label={t('annotate.sel_edit')} onClick={ann.editSelectedText} /> : null}
            {isSized(o) ? (
              <>
                <SizeButton
                  grow={false}
                  label={t('annotate.sel_smaller')}
                  disabled={ann.nextSelectedSize(-1) === sizeOf(o)}
                  onClick={() => ann.resizeSelected(-1)}
                />
                <SizeButton
                  grow
                  label={t('annotate.sel_larger')}
                  disabled={ann.nextSelectedSize(1) === sizeOf(o)}
                  onClick={() => ann.resizeSelected(1)}
                />
              </>
            ) : null}
            <BarButton icon={Palette} label={t('annotate.sel_color')} onClick={() => setColors(true)} />
            <BarButton icon={Copy} label={t('annotate.sel_duplicate')} onClick={ann.duplicateSelected} />
            <BarButton icon={Trash2} label={t('annotate.sel_delete')} onClick={ann.deleteSelected} danger />
          </>
        )}
        <span className="mx-0.5 h-8 w-px shrink-0 bg-[var(--line)]" aria-hidden />
        <button
          type="button"
          onClick={colors ? () => setColors(false) : ann.clearSelection}
          aria-label={colors ? t('common.close') : t('annotate.sel_close')}
          title={colors ? t('common.close') : t('annotate.sel_close')}
          className="flex size-11 shrink-0 items-center justify-center rounded-xl text-[var(--muted)] hover:bg-[var(--surface-2)]"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>
    </div>
  );
}
